import { supabase } from '../supabaseClient';
import { findByName } from './matching';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseNum = (v) => {
  if (v === '' || v == null) return { value: null };
  const n = parseFloat(v);
  return Number.isFinite(n) ? { value: n } : { error: `Not a number: "${v}"` };
};

const parseBoolField = (v) => {
  if (v === '' || v == null) return { value: null };
  const s = v.toString().trim().toLowerCase();
  if (['true', 'yes', 'y', '1'].includes(s)) return { value: true };
  if (['false', 'no', 'n', '0'].includes(s)) return { value: false };
  return { error: `Expected yes/no: "${v}"` };
};

function parseEntryDate(v) {
  const s = (v ?? '').toString().trim();
  if (!s) return { error: 'Date is required' };
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return { value: s };
  const d = new Date(s);
  if (isNaN(d.getTime())) return { error: `Unrecognized date: "${v}"` };
  const pad = (n) => String(n).padStart(2, '0');
  return { value: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` };
}

function parseOptionalDate(v) {
  if (!(v ?? '').toString().trim()) return { value: null };
  return parseEntryDate(v);
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// Scopes an update payload to only the columns actually mapped from the sheet, so an Update
// row never blanks out fields the file simply didn't include.
function buildUpdatePayload(mappedKeys, values) {
  const out = {};
  for (const k of mappedKeys) out[k] = values[k];
  return out;
}

const randomToken = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(32))).map(b => b.toString(16).padStart(2, '0')).join('');

// ── Clients ──────────────────────────────────────────────────────────────────

const CLIENT_FIELDS = [
  { key: 'name', label: 'Name', required: true, aliases: ['name', 'client name', 'company', 'client', 'business name'], example: 'Acme Corp' },
  { key: 'billing_name', label: 'Billing Name', aliases: ['billing name', 'legal name', 'invoice name'], example: 'Acme Corporation LLC' },
  { key: 'alias', label: 'Alias', aliases: ['alias', 'short name', 'acronym'], example: 'ACME' },
  { key: 'model', label: 'Billing Model', aliases: ['model', 'billing model'], example: 'hourly' },
  { key: 'rate', label: 'Hourly Rate', aliases: ['rate', 'hourly rate', 'rate/hr'], example: '150' },
  { key: 'retainer', label: 'Monthly Retainer', aliases: ['retainer', 'monthly retainer', 'monthly fee', 'base'], example: '' },
  { key: 'hours', label: 'Included Hours', aliases: ['included hours', 'hours'], example: '' },
  { key: 'overage', label: 'Overage Rate', aliases: ['overage', 'overage rate'], example: '' },
  { key: 'address_line1', label: 'Address Line 1', aliases: ['address', 'address line 1', 'street'], example: '123 Main St' },
  { key: 'address_line2', label: 'Address Line 2', aliases: ['address line 2', 'suite', 'unit'], example: '' },
  { key: 'city', label: 'City', aliases: ['city'], example: 'New York' },
  { key: 'state', label: 'State', aliases: ['state'], example: 'NY' },
  { key: 'zip', label: 'ZIP', aliases: ['zip', 'zip code', 'postal code'], example: '10001' },
  { key: 'country', label: 'Country', aliases: ['country'], example: 'United States' },
  { key: 'phone', label: 'Phone', aliases: ['phone', 'phone number'], example: '(555) 123-4567' },
  { key: 'billing_contact_name', label: 'Billing Contact Name', aliases: ['billing contact', 'billing contact name', 'contact name'], example: 'Jane Smith' },
  { key: 'billing_contact_email', label: 'Billing Contact Email', aliases: ['billing contact email', 'contact email', 'email', 'billing email'], example: 'billing@acmecorp.com' },
  { key: 'po_required', label: 'PO Required', aliases: ['po required', 'requires po'], example: 'no' },
  { key: 'is_nonprofit', label: 'Nonprofit', aliases: ['nonprofit', 'is nonprofit'], example: 'no' },
  { key: 'donation_percentage', label: 'Donation %', aliases: ['donation percentage', 'donation %'], example: '' },
];

async function loadClientContext(org) {
  const { data, error } = await supabase.from('clients').select('id,name').eq('org_id', org.id);
  if (error) throw error;
  return { existingClients: data || [] };
}

function buildClientPreviewRow(values, ctx) {
  const errors = [];
  const warnings = [];
  const out = {};
  // Only columns actually mapped from the sheet — used to scope Update payloads so an
  // update never blanks out an existing client's fields the sheet simply didn't include.
  const mappedKeys = new Set(Object.keys(values));

  if (!values.name?.trim()) errors.push('Name is required');
  out.name = values.name?.trim() || '';
  out.billing_name = values.billing_name?.trim() || null;
  out.alias = values.alias?.trim() || null;

  // Accept the display names (Hourly, Retainer, Retainer + overage) as well as the
  // stored ids, plus older spellings people may have in existing spreadsheets.
  const MODEL_ALIASES = { 'hourly t&m': 'hourly', 't&m': 'hourly', 'fixed': 'retainer', 'fixed retainer': 'retainer',
    'retainer + overage': 'hybrid', 'retainer+overage': 'hybrid', 'retainer plus overage': 'hybrid', 'hybrid retainer': 'hybrid' };
  const modelIn = (values.model || '').trim().toLowerCase();
  const modelRaw = MODEL_ALIASES[modelIn] || modelIn;
  if (modelRaw && !['hourly', 'retainer', 'hybrid'].includes(modelRaw)) {
    errors.push(`Unknown billing model "${values.model}" — use Hourly, Retainer, or Retainer + overage`);
  }
  out.model = ['hourly', 'retainer', 'hybrid'].includes(modelRaw) ? modelRaw : 'hourly';

  for (const [key, label] of [['rate', 'Rate'], ['retainer', 'Retainer'], ['hours', 'Hours'], ['overage', 'Overage'], ['donation_percentage', 'Donation %']]) {
    const { value, error } = parseNum(values[key]);
    if (error) errors.push(`${label}: ${error}`);
    out[key] = value ?? 0;
  }

  for (const [key, label] of [['po_required', 'PO Required'], ['is_nonprofit', 'Nonprofit']]) {
    const { value, error } = parseBoolField(values[key]);
    if (error) errors.push(`${label}: ${error}`);
    out[key] = value ?? false;
  }

  if (values.billing_contact_email?.trim()) {
    const emails = values.billing_contact_email.split(/[;,]/).map(e => e.trim()).filter(Boolean);
    const bad = emails.filter(e => !EMAIL_RE.test(e));
    if (bad.length) errors.push(`Invalid billing contact email: ${bad.join(', ')}`);
    out.billing_contact_email = values.billing_contact_email.trim();
  } else {
    out.billing_contact_email = null;
  }
  out.billing_contact_name = values.billing_contact_name?.trim() || null;
  for (const key of ['address_line1', 'address_line2', 'city', 'state', 'zip', 'country', 'phone']) {
    out[key] = values[key]?.trim() || null;
  }

  let matched = null;
  let defaultAction = 'create';
  if (out.name) {
    const hit = ctx.existingClients.find(c => c.name.trim().toLowerCase() === out.name.toLowerCase());
    if (hit) {
      matched = { id: hit.id, label: hit.name };
      defaultAction = 'skip';
      warnings.push(`Matches existing client "${hit.name}"`);
    }
  }

  return { values: out, errors, warnings, matched, defaultAction, mappedKeys };
}

async function commitClients(rows, org) {
  // Default every row to "skipped" first — anything not explicitly a create/update below
  // (including a row left on its default Skip action) resolves cleanly instead of falling
  // through with no result.
  const results = rows.map(() => ({ status: 'skipped' }));
  const creates = [];
  rows.forEach((r, i) => { if (r.effectiveAction === 'create') creates.push({ i, r }); });
  const updates = rows.map((r, i) => ({ i, r })).filter(({ r }) => r.effectiveAction === 'update');

  for (const batch of chunk(creates, 50)) {
    const payload = batch.map(({ r }) => ({ org_id: org.id, is_active: true, ...r.values }));
    const { error } = await supabase.from('clients').insert(payload);
    if (!error) {
      batch.forEach(({ i }) => { results[i] = { status: 'created' }; });
    } else {
      // Isolate the failing row(s) within this batch.
      for (const { i, r } of batch) {
        const { error: rowErr } = await supabase.from('clients').insert({ org_id: org.id, is_active: true, ...r.values });
        results[i] = rowErr ? { status: 'error', reason: rowErr.message } : { status: 'created' };
      }
    }
  }

  // clients.model/rate/retainer/hours/overage are a cache fed from
  // client_billing_terms. An update that wrote them directly would leave the
  // cache disagreeing with the terms history, so billing columns are split out
  // and applied as a billing period instead. Effective from the 1st of the
  // current month, which is how a rate edit behaved before terms existed.
  const BILLING_KEYS = ['model', 'rate', 'retainer', 'hours', 'overage'];
  const periodStart = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  })();

  for (const { i, r } of updates) {
    const nonBilling = r.mappedKeys.filter((k) => !BILLING_KEYS.includes(k));
    const billing    = r.mappedKeys.filter((k) => BILLING_KEYS.includes(k));

    if (nonBilling.length) {
      const { error } = await supabase.from('clients').update(buildUpdatePayload(nonBilling, r.values)).eq('id', r.matched.id);
      if (error) { results[i] = { status: 'error', reason: error.message }; continue; }
    }

    if (billing.length) {
      // Carry over anything the sheet did not supply, so importing only a rate
      // does not blank out a hybrid client's included hours.
      const cur = r.matched;
      const { error } = await supabase.rpc('change_client_billing_terms', {
        p_client_id: r.matched.id,
        p_effective_from: periodStart,
        p_model: r.values.model ?? cur.model ?? 'hourly',
        p_config: {
          rate:     r.values.rate     ?? cur.rate     ?? 0,
          retainer: r.values.retainer ?? cur.retainer ?? 0,
          hours:    r.values.hours    ?? cur.hours    ?? 0,
          overage:  r.values.overage  ?? cur.overage  ?? 0,
        },
        p_note: 'Bulk import',
      });
      if (error) { results[i] = { status: 'error', reason: error.message }; continue; }
    }

    results[i] = { status: 'updated' };
  }

  return results;
}

// ── Team Members ─────────────────────────────────────────────────────────────

const TEAM_FIELDS = [
  { key: 'email', label: 'Email', required: true, aliases: ['email', 'email address', 'e-mail'], example: 'jane@example.com' },
  { key: 'display_name', label: 'Name (reference only, not stored)', aliases: ['name', 'full name', 'team member'], example: 'Jane Doe' },
  { key: 'role', label: 'Role', aliases: ['role'], example: 'member' },
  { key: 'seat_type', label: 'Seat Type', aliases: ['seat type', 'seat', 'position'], example: 'contractor' },
  { key: 'compensation_rate', label: 'Compensation Rate', aliases: ['comp rate', 'compensation rate', 'pay rate'], example: '75' },
  { key: 'capacity_hours_week', label: 'Weekly Capacity (hrs)', aliases: ['capacity', 'capacity hours', 'hours/week', 'weekly capacity'], example: '' },
  { key: 'invoice_recipient_emails', label: 'AP Recipient Email(s)', aliases: ['ap email', 'invoice recipient', 'ap recipient', 'recipient emails'], example: '' },
];

async function loadTeamContext(org) {
  const { data, error } = await supabase.from('pending_invitations').select('email,status,expires_at,created_at').eq('org_id', org.id);
  if (error) throw error;
  return { invitations: data || [] };
}

function buildTeamPreviewRow(values, ctx) {
  const errors = [];
  const warnings = [];
  const out = {};

  const email = values.email?.trim().toLowerCase() || '';
  if (!email) errors.push('Email is required');
  else if (!EMAIL_RE.test(email)) errors.push(`Invalid email: "${values.email}"`);
  out.email = email;

  const roleRaw = (values.role || 'member').trim().toLowerCase();
  if (!['member', 'admin'].includes(roleRaw)) errors.push(`Role must be "member" or "admin" (owner cannot be bulk-imported) — got "${values.role}"`);
  out.role = ['member', 'admin'].includes(roleRaw) ? roleRaw : 'member';

  const seatRaw = (values.seat_type || 'contractor').trim().toLowerCase();
  const validSeats = ['contractor', 'employee_hourly', 'employee_salary', 'admin'];
  if (!validSeats.includes(seatRaw)) errors.push(`Unknown seat type "${values.seat_type}" — must be one of ${validSeats.join(', ')}`);
  out.seat_type = validSeats.includes(seatRaw) ? seatRaw : 'contractor';

  const { value: compRate, error: compErr } = parseNum(values.compensation_rate);
  if (compErr) errors.push(`Compensation rate: ${compErr}`);
  out.compensation_rate = compRate ?? 0;

  const { value: capacity, error: capErr } = parseNum(values.capacity_hours_week);
  if (capErr) errors.push(`Weekly capacity: ${capErr}`);
  out.capacity_hours_week = capacity;

  out.invoice_recipient_emails = values.invoice_recipient_emails?.trim() || null;
  out.display_name = values.display_name?.trim() || null;

  let defaultAction = 'create';
  if (email) {
    // Best-effort duplicate detection: org_members doesn't expose member emails client-side,
    // so an existing invitation record (accepted or pending) is the signal we have.
    const invRows = ctx.invitations.filter(i => i.email.trim().toLowerCase() === email);
    const accepted = invRows.find(i => i.status === 'accepted');
    const pending = invRows.find(i => i.status === 'pending' && new Date(i.expires_at) > new Date());
    if (accepted) { defaultAction = 'skip'; warnings.push('Already a member (an invitation to this email was already accepted)'); }
    else if (pending) { defaultAction = 'skip'; warnings.push('Invitation already pending for this email'); }
  }

  return { values: out, errors, warnings, matched: null, defaultAction };
}

async function commitTeamMembers(rows, org, profile) {
  const results = new Array(rows.length);
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (r.effectiveAction !== 'create') { results[i] = { status: 'skipped' }; continue; }

    // Re-check prior tenure at commit time in case org state shifted since preview.
    const { data: prior } = await supabase.rpc('find_prior_tenure', { p_org_id: org.id, p_email: r.values.email });
    if (prior && prior.length > 0) {
      results[i] = { status: 'error', reason: `Previously off-boarded (ended ${prior[0].ended_at}) — use Reactivate from the Team page instead` };
      continue;
    }

    const token = randomToken();
    const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await supabase.from('pending_invitations').insert({
      org_id: org.id, email: r.values.email, role: r.values.role, seat_type: r.values.seat_type,
      compensation_rate: r.values.compensation_rate, capacity_hours_week: r.values.capacity_hours_week,
      token, expires_at: expiresAt, invited_by: profile.id, status: 'pending',
    });
    if (error) { results[i] = { status: 'error', reason: error.message }; continue; }

    const inviteUrl = `https://usetimekeep.com/accept-invite?token=${token}`;
    try {
      await supabase.functions.invoke('send-invitation', {
        body: { invitee_email: r.values.email, org_id: org.id, org_name: org.name, role: r.values.role, seat_type: r.values.seat_type, invite_url: inviteUrl },
      });
      results[i] = { status: 'created' };
    } catch (e) {
      results[i] = { status: 'created', reason: 'Invitation saved, but the email failed to send — resend it from the Team page' };
    }
  }
  return results;
}

// ── Time Entries ──────────────────────────────────────────────────────────────

const TIME_ENTRY_FIELDS = [
  { key: 'team_member', label: 'Team Member', required: true, aliases: ['team member', 'member', 'employee', 'contractor', 'person', 'name'], example: 'Jane Doe' },
  { key: 'entry_date', label: 'Date', required: true, aliases: ['date', 'entry date', 'work date'], example: '2026-08-01' },
  { key: 'hours', label: 'Hours', required: true, aliases: ['hours', 'hrs', 'time'], example: '2.5' },
  { key: 'client', label: 'Client', aliases: ['client', 'client name'], example: 'Acme Corp' },
  { key: 'task_type', label: 'Task Type', aliases: ['task type', 'task', 'category'], example: 'Development' },
  { key: 'description', label: 'Description', aliases: ['description', 'notes', 'details', 'memo'], example: 'Weekly status call' },
  { key: 'is_billable', label: 'Billable', aliases: ['billable', 'is billable'], example: 'yes' },
];

async function loadTimeEntryContext(org) {
  const [members, clients, taskTypes, locks] = await Promise.all([
    supabase.from('org_members').select('user_id, profiles(name)').eq('org_id', org.id).is('ended_at', null),
    supabase.from('clients').select('id,name').eq('org_id', org.id),
    supabase.from('task_types').select('id,name').eq('org_id', org.id),
    supabase.from('month_locks').select('user_id,client_id,period').eq('org_id', org.id).is('reopened_at', null),
  ]);
  for (const r of [members, clients, taskTypes, locks]) if (r.error) throw r.error;
  return {
    members: (members.data || []).map(m => ({ user_id: m.user_id, name: m.profiles?.name || '' })).filter(m => m.name),
    clients: clients.data || [],
    taskTypes: taskTypes.data || [],
    locks: locks.data || [],
  };
}

function buildTimeEntryPreviewRow(values, ctx, seenInBatch) {
  const errors = [];
  const warnings = [];
  const out = {};

  const memberName = values.team_member?.trim() || '';
  if (!memberName) errors.push('Team Member is required');
  let member = null;
  if (memberName) {
    const { match, suggestion, ambiguous } = findByName(memberName, ctx.members, 'name');
    if (match) member = match;
    else if (ambiguous) errors.push(`Multiple team members are named "${memberName}" — ambiguous, can't resolve which one`);
    else errors.push(`Unknown team member "${memberName}"${suggestion ? ` — did you mean "${suggestion}"?` : ' — must match a name on the Team page exactly'}`);
  }
  out.user_id = member?.user_id || null;
  out.team_member_label = memberName;

  const { value: entryDate, error: dateErr } = parseEntryDate(values.entry_date);
  if (dateErr) errors.push(dateErr);
  out.entry_date = entryDate || null;

  const { value: hours, error: hoursErr } = parseNum(values.hours);
  if (hoursErr) errors.push(`Hours: ${hoursErr}`);
  else if (hours == null || hours <= 0) errors.push('Hours must be greater than 0');
  out.hours = hours;

  let clientMatch = null;
  const clientName = values.client?.trim() || '';
  if (clientName) {
    const { match, suggestion, ambiguous } = findByName(clientName, ctx.clients, 'name');
    if (match) clientMatch = match;
    else if (ambiguous) errors.push(`Multiple clients are named "${clientName}" — ambiguous, can't resolve which one`);
    else errors.push(`Unknown client "${clientName}"${suggestion ? ` — did you mean "${suggestion}"?` : ' — add it first or fix the name'}`);
  }
  out.client_id = clientMatch?.id || null;
  out.client_label = clientName;

  let taskMatch = null;
  const taskName = values.task_type?.trim() || '';
  if (taskName) {
    const { match, suggestion, ambiguous } = findByName(taskName, ctx.taskTypes, 'name');
    if (match) taskMatch = match;
    else if (ambiguous) errors.push(`Multiple task types are named "${taskName}" — ambiguous, can't resolve which one`);
    else errors.push(`Unknown task type "${taskName}"${suggestion ? ` — did you mean "${suggestion}"?` : ' — add it first or fix the name'}`);
  }
  out.task_type_id = taskMatch?.id || null;

  out.description = values.description?.trim() || '';

  const { value: billable, error: billErr } = parseBoolField(values.is_billable);
  if (billErr) errors.push(`Billable: ${billErr}`);
  out.is_billable = billable ?? true;

  if (out.user_id && out.client_id && out.entry_date) {
    const period = out.entry_date.slice(0, 7);
    const locked = ctx.locks.some(l => l.user_id === out.user_id && l.client_id === out.client_id && l.period === period);
    if (locked) errors.push('This period is locked for that client — reopen it first or fix the date');
  }

  if (!errors.length) {
    const dupeKey = `${out.user_id}|${out.client_id}|${out.task_type_id}|${out.entry_date}|${out.hours}|${out.description}`;
    if (seenInBatch.has(dupeKey)) warnings.push('Possible duplicate of another row in this file');
    seenInBatch.add(dupeKey);
  }

  return { values: out, errors, warnings, matched: null, defaultAction: 'create' };
}

async function commitTimeEntries(rows, org) {
  const results = rows.map(() => ({ status: 'skipped' }));
  const creates = rows.map((r, i) => ({ i, r })).filter(({ r }) => r.effectiveAction === 'create');

  for (const batch of chunk(creates, 20)) {
    const payload = batch.map(({ r }) => ({
      org_id: org.id, user_id: r.values.user_id, client_id: r.values.client_id, task_type_id: r.values.task_type_id,
      description: r.values.description, hours: r.values.hours, entry_date: r.values.entry_date, is_billable: r.values.is_billable,
    }));
    const { error } = await supabase.from('time_entries').insert(payload);
    if (!error) {
      batch.forEach(({ i }) => { results[i] = { status: 'created' }; });
    } else {
      for (const { i, r } of batch) {
        const { error: rowErr } = await supabase.from('time_entries').insert({
          org_id: org.id, user_id: r.values.user_id, client_id: r.values.client_id, task_type_id: r.values.task_type_id,
          description: r.values.description, hours: r.values.hours, entry_date: r.values.entry_date, is_billable: r.values.is_billable,
        });
        results[i] = rowErr ? { status: 'error', reason: rowErr.message } : { status: 'created' };
      }
    }
  }
  return results;
}

// ── Invoices (historical/already-issued invoices — not new ones to send) ────────

const INVOICE_FIELDS = [
  { key: 'client', label: 'Client', required: true, aliases: ['client', 'client name'], example: 'Acme Corp' },
  { key: 'invoice_number', label: 'Invoice Number', required: true, aliases: ['invoice number', 'invoice #', 'invoice no', 'number'], example: 'INV-2025-014' },
  { key: 'period', label: 'Period (YYYY-MM)', required: true, aliases: ['period', 'billing period', 'month'], example: '2025-11' },
  { key: 'issued_date', label: 'Issued Date', required: true, aliases: ['issued date', 'invoice date', 'date'], example: '2025-11-01' },
  { key: 'total', label: 'Total', required: true, aliases: ['total', 'amount', 'invoice total'], example: '4500' },
  { key: 'status', label: 'Status', aliases: ['status'], example: 'paid' },
  { key: 'due_date', label: 'Due Date', aliases: ['due date'], example: '2025-12-01' },
  { key: 'paid_date', label: 'Paid Date', aliases: ['paid date', 'date paid', 'payment date'], example: '2025-11-20' },
  { key: 'subtotal', label: 'Subtotal', aliases: ['subtotal'], example: '' },
  { key: 'payment_terms', label: 'Payment Terms', aliases: ['payment terms', 'terms'], example: 'Net 30' },
  { key: 'po_number', label: 'PO Number', aliases: ['po number', 'po #', 'purchase order'], example: '' },
  { key: 'notes', label: 'Notes', aliases: ['notes', 'memo'], example: '' },
  { key: 'description', label: 'Description (one line item)', aliases: ['description', 'line item'], example: 'November retainer' },
];

async function loadInvoiceContext(org) {
  const [clients, invoices] = await Promise.all([
    supabase.from('clients').select('id,name').eq('org_id', org.id),
    supabase.from('invoices').select('id,client_id,invoice_number').eq('org_id', org.id),
  ]);
  if (clients.error) throw clients.error;
  if (invoices.error) throw invoices.error;
  return { clients: clients.data || [], invoices: invoices.data || [] };
}

function buildInvoicePreviewRow(values, ctx) {
  const errors = [];
  const warnings = [];
  const out = {};
  const mappedKeys = new Set(Object.keys(values));

  let clientMatch = null;
  const clientName = values.client?.trim() || '';
  if (!clientName) errors.push('Client is required');
  else {
    const { match, suggestion, ambiguous } = findByName(clientName, ctx.clients, 'name');
    if (match) clientMatch = match;
    else if (ambiguous) errors.push(`Multiple clients are named "${clientName}" — ambiguous, can't resolve which one`);
    else errors.push(`Unknown client "${clientName}"${suggestion ? ` — did you mean "${suggestion}"?` : ' — add it first or fix the name'}`);
  }
  out.client_id = clientMatch?.id || null;
  out.client_label = clientName;

  const invoiceNumber = values.invoice_number?.trim() || '';
  if (!invoiceNumber) errors.push('Invoice Number is required');
  out.invoice_number = invoiceNumber;

  const period = values.period?.trim() || '';
  if (!/^\d{4}-\d{2}$/.test(period)) errors.push(`Period must be YYYY-MM — got "${values.period || ''}"`);
  out.period = period;

  const { value: issuedDate, error: issuedErr } = parseEntryDate(values.issued_date);
  if (issuedErr) errors.push(`Issued Date: ${issuedErr}`);
  out.issued_date = issuedDate || null;

  const { value: total, error: totalErr } = parseNum(values.total);
  if (totalErr) errors.push(`Total: ${totalErr}`);
  else if (total == null) errors.push('Total is required');
  else if (total < 0) errors.push('Total cannot be negative');
  out.total = total ?? 0;

  const { value: subtotal, error: subtotalErr } = parseNum(values.subtotal);
  if (subtotalErr) errors.push(`Subtotal: ${subtotalErr}`);
  out.subtotal = subtotal ?? out.total;

  const statusRaw = (values.status || 'draft').trim().toLowerCase();
  const statusMap = { draft: 'draft', sent: 'sent', invoiced: 'sent', unpaid: 'sent', outstanding: 'sent', open: 'sent', paid: 'paid' };
  if (!(statusRaw in statusMap)) errors.push(`Unknown status "${values.status}" — must be draft, sent (or unpaid/outstanding/open), or paid`);
  out.status = statusMap[statusRaw] || 'draft';

  const { value: dueDate, error: dueErr } = parseOptionalDate(values.due_date);
  if (dueErr) errors.push(`Due Date: ${dueErr}`);
  out.due_date = dueDate;

  const { value: paidDate, error: paidErr } = parseOptionalDate(values.paid_date);
  if (paidErr) errors.push(`Paid Date: ${paidErr}`);
  if (out.status === 'paid') {
    if (paidDate) out.paid_at = paidDate;
    else { out.paid_at = out.issued_date; warnings.push('No paid date given — defaulting to issued date'); }
  } else {
    out.paid_at = null;
  }

  out.payment_terms = values.payment_terms?.trim() || null;
  out.po_number = values.po_number?.trim() || null;
  out.notes = values.notes?.trim() || null;
  out.description = values.description?.trim() || '';

  let matched = null;
  let defaultAction = 'create';
  if (out.client_id && out.invoice_number) {
    const hit = ctx.invoices.find(i => i.client_id === out.client_id && i.invoice_number.trim().toLowerCase() === out.invoice_number.toLowerCase());
    if (hit) {
      matched = { id: hit.id, label: hit.invoice_number };
      defaultAction = 'skip';
      warnings.push(`Matches existing invoice "${hit.invoice_number}" for this client`);
    }
  }

  return { values: out, errors, warnings, matched, defaultAction, mappedKeys };
}

async function commitInvoices(rows, org) {
  const results = rows.map(() => ({ status: 'skipped' }));
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (r.effectiveAction === 'create') {
      const { description, client_label, ...invoiceFields } = r.values;
      const { data, error } = await supabase.from('invoices').insert({
        org_id: org.id, line_item_mode: 'manual', skip_reminders: true, recurring: false,
        ...invoiceFields,
      }).select('id').single();
      if (error) { results[i] = { status: 'error', reason: error.message }; continue; }
      if (description) {
        const { error: lineErr } = await supabase.from('invoice_line_items').insert({
          invoice_id: data.id, description, hours: null, rate: null, amount: r.values.total, sort_order: 0,
        });
        if (lineErr) { results[i] = { status: 'created', reason: `Invoice saved, but its line item failed: ${lineErr.message}` }; continue; }
      }
      results[i] = { status: 'created' };
    } else if (r.effectiveAction === 'update') {
      // r.mappedKeys holds the *sheet* field keys, which don't all match the invoices column
      // names 1:1 ("client" -> client_id, "paid_date" -> paid_at, "description" isn't a column
      // at all) — translate before scoping the payload to what was actually mapped.
      const FIELD_TO_COLUMN = { client: 'client_id', paid_date: 'paid_at' };
      const payload = {};
      for (const k of r.mappedKeys) {
        if (k === 'description') continue; // handled as a separate line-item insert, not a column
        const col = FIELD_TO_COLUMN[k] || k;
        payload[col] = r.values[col];
      }
      const { error } = await supabase.from('invoices').update(payload).eq('id', r.matched.id);
      results[i] = error ? { status: 'error', reason: error.message } : { status: 'updated' };
    }
  }
  return results;
}

// ── Registry ─────────────────────────────────────────────────────────────────

export const ENTITY_CONFIGS = {
  clients: {
    label: 'Clients',
    fields: CLIENT_FIELDS,
    previewKeys: ['name', 'model', 'rate'],
    docsAnchor: 'clients',
    loadContext: loadClientContext,
    buildPreviewRow: buildClientPreviewRow,
    commit: (rows, org) => commitClients(rows, org),
  },
  team: {
    label: 'Team Members',
    fields: TEAM_FIELDS,
    previewKeys: ['email', 'role', 'seat_type'],
    docsAnchor: 'team-members',
    loadContext: loadTeamContext,
    buildPreviewRow: buildTeamPreviewRow,
    commit: (rows, org, profile) => commitTeamMembers(rows, org, profile),
  },
  time_entries: {
    label: 'Time Entries',
    fields: TIME_ENTRY_FIELDS,
    previewKeys: ['team_member_label', 'client_label', 'entry_date', 'hours'],
    docsAnchor: 'time-entries',
    loadContext: loadTimeEntryContext,
    buildPreviewRow: (values, ctx, seen) => buildTimeEntryPreviewRow(values, ctx, seen),
    commit: (rows, org) => commitTimeEntries(rows, org),
  },
  invoices: {
    label: 'Invoices',
    fields: INVOICE_FIELDS,
    previewKeys: ['client_label', 'invoice_number', 'period', 'total'],
    docsAnchor: 'invoices',
    loadContext: loadInvoiceContext,
    buildPreviewRow: buildInvoicePreviewRow,
    commit: (rows, org) => commitInvoices(rows, org),
  },
};
